import { useResetOnClose } from "@/lib/hooks/useResetOnClose"
import React, { FC, Dispatch, SetStateAction, useState } from "react"
import {
  CalendarDaysIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline"
import { Table } from "@/components/general/Table"
import { HolidayModiferType } from "@/lib/types"
import { Modal } from "@/components/general/Modal"
import { useForm } from "react-hook-form"
import Swal from "sweetalert2"
import { dateFormat1 } from "@/lib/services"
import { dateFormat3 } from "@/lib/services/Format"
import DatePicker from "react-datepicker"

type Props = {
  popup: boolean
  setPopup: Dispatch<SetStateAction<boolean>>
  listingData: { data: HolidayModiferType[] }
  header: {
    name: string
    width: string
  }[]
  handleForm: (form: HolidayModiferType) => void
  handleFormUpdate: (form: HolidayModiferType) => void
  handleFormDelete: (form: string) => void
  title: string
  setTitle: (title: string) => void
}

export const HolidayModifier: FC<Props> = (props) => {
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<HolidayModiferType>()

  const onSubmit = (data: HolidayModiferType) => {
    data.startDate = dateFormat3(startDate as Date)
    data.endDate = dateFormat3(endDate !== null ? endDate : (startDate as Date))

    if (data.id == undefined) {
      props.handleForm(data)
    } else {
      props.handleFormUpdate(data)
    }
  }

  const onChange = (dates: [Date, Date]) => {
    const [start, end] = dates
    setStartDate(start)
    setEndDate(end)
  }

  const onDelete = (id: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      // color: "#ffffff",
      // background: "#333333",
      showCancelButton: true,
      confirmButtonColor: "#CEA16B",
      cancelButtonColor: "#a33333",
      customClass: {
        popup: 'custom-popup',
      },
      confirmButtonText: "<p style='color: black;'>Yes, Delete it!</p>",
    }).then(async (result) => {
      if (result.isConfirmed) {
        props.handleFormDelete(id)
        Swal.fire({
          icon: "success",
          title: "Record Deleted",
          // background: "#333333",
          toast: true,
          position: "top",
          timer: 2000,
          iconColor: "#CEA16B",
          showCancelButton: false,
          showConfirmButton: false,
          customClass: {
            popup: "custom-success-popup",
          }
        })

      }
    })
  }

  const populateData = (e: HolidayModiferType) => {
    props.setTitle('Update Holiday Modifer')
    const endDate = new Date(e.endDate)
    endDate.setDate(endDate.getDate() - 1)
    setValue("id", e.id)
    setStartDate(new Date(e.startDate))
    setEndDate(endDate)
    setValue("holidayName", e.holidayName)
    setValue("holidayValue", e.holidayValue)
    props.setPopup(true)
  }

  useResetOnClose(props.popup, () => {
    reset({
      holidayName: "",
      holidayValue: undefined,
      id: undefined,
    })
    setStartDate(null)
    setEndDate(null)
  })

  return (
    <>
      <Modal
        open={props.popup}
        close={() => props.setPopup(false)}
        width="x-small"
        title={props.title}
      >
        <div className="flex flex-col gap-3 z-20">
          <fieldset className="w-full z-40">
            <label className="text-xs dark:text-primary-8 text-light-1">Holiday Date</label>
            <DatePicker
              className="dark:bg-transparent bg-white dark:text-primary-2 text-light-2 border-2 dark:border-secondary-2 rounded outline-none placeholder:text-xs z-10"
              placeholderText="Select Date"
              showIcon
              onChange={onChange}
              startDate={startDate}
              endDate={endDate}
              selectsRange
              dateFormat="MM/dd/yyyy"
              icon={<CalendarDaysIcon className="dark:text-primary-8 text-light-1 w-full" />}
            />
          </fieldset>
          <div className="grid grid-cols-2 gap-2 place-items-center">
            <fieldset className="w-full">
              <label className="text-xs dark:text-primary-8 text-light-1">Holiday Name</label>
              <input
                className={
                  (errors.holidayName ? "border-red-400" : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white outline-none dark:text-white text-light-2 border-2 rounded placeholder:text-xs"
                }
                type="text"
                placeholder="Enter Holiday Name"
                {...register("holidayName", { required: true })}
              />
            </fieldset>
            <fieldset className="w-full">
              <label className="text-xs dark:text-primary-8 text-light-1">Holiday Value</label>
              <input
                className={
                  (errors.holidayValue ? "border-red-400" : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white outline-none dark:text-white text-light-2  border-2 rounded placeholder:text-xs"
                }
                type="number"
                placeholder="Enter Holiday Value"
                {...register("holidayValue", { required: true })}
              />
            </fieldset>
          </div>
          <button
            onClick={handleSubmit(onSubmit)}
            className="bg-primary-8 px-4 py-2 rounded font-medium w-full dark:text-secondary-1 text-primary-1"
            type="button"
          >
            Submit
          </button>
        </div>
      </Modal>
      <div className="m-4">
        <Table
          header={props.header}
          headerAlign="text-left"
          lastheadingAlign="text-right"
        >
          {props.listingData.data.map((e, i) => (
            <tr key={i} className="text-left">
              <td className={"whitespace-nowrap p-4 w-52"}>
                {dateFormat1(e.startDate)} - {dateFormat1(e.endDate)}
              </td>
              <td className={"whitespace-nowrap p-4 w-52"}>{e.holidayName}</td>
              <td className={"whitespace-nowrap p-4 w-52"}>{e.holidayValue}</td>
              <td className={"whitespace-nowrap p-4 w-52"}>
                <div className="flex justify-end gap-2">
                  <PencilSquareIcon
                    className="w-4 h-4 text-accent cursor-pointer"
                    onClick={() => populateData(e)}
                  />
                  <TrashIcon
                    className="w-4 h-4 text-accent cursor-pointer"
                    onClick={() => onDelete(e.id)}
                  />
                </div>
              </td>
            </tr>
          ))}
        </Table>
      </div>
    </>
  )
}
